import { useSearchParams } from 'react-router-dom'

import { isSameDay, isValid } from 'date-fns'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { dateToString } from 'modules/core/lib/dates'

import type HeadcountEvolution from 'modules/insights/lib/metrics/headcountEvolution'

const SELECTED_RANGE_PARAM = 'selectedRange'

export const useSelectedRange = (headcountEvolution: HeadcountEvolution) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedRangeParam = searchParams.get(SELECTED_RANGE_PARAM)

  const setSelectedRange = useUnsafeCallback(
    (newXAxis: Date) => {
      setSearchParams(
        (previousSearchParams) => {
          previousSearchParams.set(SELECTED_RANGE_PARAM, dateToString(newXAxis, 'backend'))

          return previousSearchParams
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const selectedRange = useUnsafeMemo(() => {
    const lastDateString =
      headcountEvolution.headcountEvolution[headcountEvolution.headcountEvolution.length - 1]
        ?.endDate

    // The last date will be used as the fallback
    const lastDate = lastDateString ? parseBackendDate(lastDateString) : undefined

    if (!selectedRangeParam) {
      return lastDate
    }

    const parsedSelectedRange = parseBackendDate(selectedRangeParam)

    // Check if the selected range is valid and exists in the data
    if (
      isValid(parsedSelectedRange) &&
      headcountEvolution.headcountEvolution.find((range) =>
        isSameDay(parseBackendDate(range.endDate), parsedSelectedRange)
      )
    ) {
      return parsedSelectedRange
    }

    return lastDate
  }, [selectedRangeParam, headcountEvolution.headcountEvolution])

  return {
    selectedRange,
    setSelectedRange,
  }
}
